/**
 * Pure back-navigation decision logic for the Issue detail component.
 *
 * Extracted from IssueBase so it can be unit-tested without React overhead.
 * The caller is responsible for executing the returned slide action and navigation.
 */

/** Describes what navigation should happen when the user presses back in Issue. */
export type BackRoutingDecision =
  | {
      /** Collapse to the leftmost single column, then navigate to the activities list. */
      slide: 'SINGLE';
      url: string;
      state: { skipAutoSelect: true };
    }
  | {
      /** Slide back one column and navigate to the derived parent URL. */
      slide: 'PREV';
      url: string;
    };

/**
 * Computes the back-navigation destination for an Issue panel.
 *
 * Decision table (segments = pathname.split('/').filter(Boolean)):
 *
 *   URL (segments)                                                segs  action
 *   ----------------------------------------------------------   ----  ------
 *   /activities/123                                               2     → activities list (SINGLE)
 *   /activities/123/edit                                          3     → /activities/123  (PREV, -1)
 *   /assets/456/activity/123                                      4     → /assets/456       (PREV, -2)
 *   /assets/456/activity/123/edit                                 5     → /assets/456       (PREV, -3)
 *   /activities/123/asset/456/activity/789                        6     → /activities/123/asset/456 (PREV, -2)
 *   /activities/123/asset/456/activity/789/edit                   7     → /activities/123/asset/456 (PREV, -3)
 *
 * Rules:
 *   ≤ 2 segments → activities list
 *     3 segments → remove 1  (shallow edit — go up to the detail page)
 *   4+ segments, even count → remove 2 (paired :type/:id leaf)
 *   5+ segments, odd count  → remove 3 (action sub-page — skip past :id + action)
 *
 * @param pathname       - Current `location.pathname`
 * @param fromParam      - Value of the `from` query-string param (or null)
 * @param activityRoot   - The root path segment for activities (e.g. 'activities')
 * @param allActivitiesQueryValue - The query value that signals "came from all-activities list"
 */
export function computeBackRoute(
  pathname: string,
  fromParam: string | null,
  activityRoot: string,
  allActivitiesQueryValue: string,
): BackRoutingDecision {
  const segments = pathname.split('/').filter(Boolean);

  // Special case: arrived via the "all activities" list — go back to the all-activities view.
  if (fromParam === allActivitiesQueryValue) {
    const url = '/' + segments.slice(0, -1).concat('all').join('/');
    return { slide: 'PREV', url };
  }

  if (segments.length <= 2) {
    // At the activity detail home page (/activities/:id) — go back to the list.
    return {
      slide: 'SINGLE',
      url: `/${activityRoot}`,
      state: { skipAutoSelect: true },
    };
  }

  // Determine how many trailing segments to drop.
  let segmentsToRemove: number;
  if (segments.length === 3) {
    // e.g. /activities/:id/edit → go up 1 to /activities/:id
    segmentsToRemove = 1;
  } else {
    // Deeper paths: even depth = paired id (drop 2), odd depth = action page (drop 3).
    segmentsToRemove = segments.length % 2 === 0 ? 2 : 3;
  }

  const targetSegments = segments.slice(0, segments.length - segmentsToRemove);

  if (targetSegments.length < 2) {
    // Would land at or above root — fall back to the activities list.
    return {
      slide: 'SINGLE',
      url: `/${activityRoot}`,
      state: { skipAutoSelect: true },
    };
  }

  return { slide: 'PREV', url: '/' + targetSegments.join('/') };
}
